<script setup lang="ts">
import { fournirRegimeFiscal } from "~/composables/useRegimeFiscal";
import { champNomAffiche } from "~/utils/champNomDonateur";
import DonateMoyensLocaux from "~/components/donate/DonateMoyensLocaux.vue";
import { useMoyensLocauxDon } from "~/composables/payment/useMoyensLocauxDon";
import { cleTexteTitulaire } from "~/utils/texteTitulaireMoyenLocal";
import { isPaypalCardFieldsUsable, resolveCardProvider } from "@donoor/checkout";
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { isValidEmail } from "@donoor/validators";
import { formatInParis, jourDeFinCagnotte, MOYENS_PAIEMENT_LOCAUX } from "@donoor/utils";
import { darkenColor } from "@donoor/utils/color";
import { logger } from "~/utils/logger";
import { suffixePeriodeImpact } from "~/utils/montantsImpact";
import { useCampaignPaymentSeo } from "~/composables/payment/useCampaignPaymentSeo";

definePageMeta({
  layout: "payment",
});

const route = useRoute();
const router = useRouter();
const campaignSlug = ((route.params as Record<string, string>).slug ?? "") as string;
const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

// SSR fetch + SEO/OG meta tags — extrait dans useCampaignPaymentSeo
await useCampaignPaymentSeo(campaignSlug);

// ============================================
// Client: Composables pour l'interactivité
// ============================================

// Montant du don à Donoor (en euros) - proportionnel au montant du don
const donateToUsAmount = ref<number>(getDonoorTipAmount(10));

// Composables - utilise le slug au lieu de l'ID
const campaignData = useCampaignDonationData(ref(campaignSlug));

/**
 * « Paiement sécurisé par Stripe / PayPal / Stripe et PayPal » sous le bouton,
 * comme les autres pages de don — au lieu de « Payez en toute sécurité à … »,
 * qui répétait le nom de l'association (Karim 02/11/2026).
 */
const libelleSecurite = computed(() => {
  const pp = campaignData.paymentProviders.value;
  const stripe = pp?.stripe?.charges_enabled === true;
  const paypal = pp?.paypal?.charges_enabled === true;
  if (stripe && paypal) return t("don.checkoutOrchestrateur.stripeEtPaypal");
  return stripe ? "Stripe" : paypal ? "PayPal" : "";
});

// `contextIds` : rattache la transaction à SA cagnotte. L'id arrive d'un fetch
// async (useCampaignDonationData) PAS encore résolu au setup → on DOIT le lire
// paresseusement via un getter, sinon il est figé à 0/"" et le backend écrit
// campaign_id = NULL → la jauge "montant récolté" reste à 0 €. Même pattern
// que /pay/[slug].vue (qrContextIds). Bug équipe TestVF 2026-06-24.
const campaignContextIds = {
  get campaign_id(): number | undefined {
    return campaignData.campaignInfo.value?.id ?? undefined;
  },
};
const paymentForm = usePaymentForm();

// Montant choisi sur la page de la cagnotte (bouton « Faire un don » →
// /c/<slug>/payment?montant=25, Karim 2026-10-12) : il remplace le montant
// par défaut. Même plancher que la validation du formulaire (1 €).
const montantDemande = (() => {
  const brut = route.query.montant;
  const n = Number(Array.isArray(brut) ? brut[0] : brut);
  return Number.isFinite(n) && n >= 1 && n <= 1_000_000 ? Math.round(n * 100) / 100 : null;
})();
if (montantDemande !== null) {
  paymentForm.form.value.amount = String(montantDemande);
  // Le pourboire suit le montant (même règle que le recalcul plus bas).
  donateToUsAmount.value = getDonoorTipAmount(montantDemande);
}
const { estimateProvider, paypalFeePercent, paypalFeeFixed } =
  usePaypalFeeEstimate(campaignData.paymentProviders, paymentForm.isSubscription);
const calculations = usePaymentCalculations({
  amount: computed(() => paymentForm.form.value.amount),
  includeFees: paymentForm.includeFees,
  donoorFeeRate: campaignData.donoorFeeRate,
  isSubscription: paymentForm.isSubscription,
  subscriptionPeriod: paymentForm.subscriptionPeriod,
  donateToUsAmount,
  paymentProvider: estimateProvider,
  paypalFeePercent,
  paypalFeeFixed,
});

// « L'association reçoit … » : le don MOINS les frais quand le donateur ne
// les couvre pas (bug 14/10/2026 : on affichait le don entier), avec le
// détail de ce qui est retiré (frais du prestataire, commission Donoor).
const detailAssociation = useNetAssociationEstime({
  montant: calculations.finalAmount,
  soutienDonoor: donateToUsAmount,
  couvreFrais: paymentForm.includeFees,
  tauxDonoor: campaignData.donoorFeeRate,
  prestataire: estimateProvider,
  paypalPourcent: paypalFeePercent,
  paypalFixe: paypalFeeFixed,
});
// ─────────────────────────────────────────────────────────────────────────
// Stripe Checkout — factorisé via useStripeDonationCheckout.
// Pas de subscriptionHandlers : cagnottes = dons uniques uniquement
// (cf. memory/product_campaigns_no_recurring.md).
// ─────────────────────────────────────────────────────────────────────────
const stripeDonation = useStripeDonationCheckout({
  couleur: () => primaryColor.value,
  paymentForm,
  calculations,
  organizationId: campaignData.organizationId,
  stripeAccountId: campaignData.stripeAccountId,
  donateUs: campaignData.donateUs,
  donateToUsAmount,
  // Getter (cf. campaignContextIds) → l'id est lu au moment du checkout, après
  // résolution du fetch cagnotte, jamais figé à 0.
  contextIds: campaignContextIds,
  enableSubscriptions: false,
  successUrl: (email) => {
    const info = campaignData.campaignInfo.value;
    const params = new URLSearchParams({
      campaign_id: String(info?.id || ""),
      campaign_title: info?.title || "",
      thank_you_message: info?.customThankYouMessage || "",
    });
    if (email) params.set("email", email);
    return `/donation-success?${params.toString()}`;
  },
});

const checkout = stripeDonation.checkout;
const currentPaymentIntentId = stripeDonation.currentPaymentIntentId;
// Provenance du lien de départ (utm, gclid…) → webhooks de l'association.
// Lecture seule de l'identifiant, envoi isolé (cf. useProvenance).
suivreProvenancePaiement(currentPaymentIntentId);

// Environnement
const runtimeConfig = useRuntimeConfig();
const isDev = computed(() => runtimeConfig.public.mode === "dev");

// ─────────────────────────────────────────────────────────────────────────
// PayPal checkout (Connected Path Multiparty) — via composable réutilisable
// Note : on utilise `campaignData.campaignInfo` directement plutôt que
// `campaignInfo` (déclaré plus bas) pour respecter le hoisting TS.
// ─────────────────────────────────────────────────────────────────────────
const paypalDonation = usePaypalDonationCheckout({
  couleur: () => primaryColor.value,
  paymentProviders: campaignData.paymentProviders,
  paymentForm,
  calculations,
  donateUs: campaignData.donateUs,
  donateToUsAmount,
  organizationId: campaignData.organizationId,
  // Même getter réactif que Stripe → campaign_id lu au checkout, jamais figé.
  contextIds: campaignContextIds,
  successUrl: (email) => {
    const info = campaignData.campaignInfo.value;
    const params = new URLSearchParams({
      campaign_id: String(info?.id || ""),
      campaign_title: info?.title || "",
      thank_you_message: info?.customThankYouMessage || "",
    });
    if (email) params.set("email", email);
    return `/donation-success?${params.toString()}`;
  },
});

// Alias pour compatibilité avec le template
const pageLoading = computed(() => campaignData.isLoading.value);
const isCampaignValid = campaignData.isCampaignValid;
const isCampaignPaused = campaignData.isCampaignPaused;
const campaignError = campaignData.campaignError;
const errorTitle = campaignData.errorTitle;
// cardFormError : uniquement les erreurs de montage du formulaire carte.
// Distinct de checkout.error (wallets CORS + soumissions) pour éviter
// l'écran fullscreen sur erreurs PayPal Apple/Google Pay ou CVC incomplet.
const stripeInitError = computed(() => checkout.cardFormError.value !== null);
const stripeErrorMessage = computed(() => checkout.cardFormError.value?.message || "");

// ─── Classification erreur récupérable vs permanente (toast inline) ────
// Cf. useProvidersOrchestrator.ts pour la logique de référence + doc.
// Une erreur réseau / 429 / 5xx est récupérable → toast inline (le user
// garde accès à la page). Une erreur de config (clé invalide, etc.) est
// permanente → écran plein-page (DonateErrorStates).
function isRecoverableErrMsg(msg: string): boolean {
  const m = msg.toLowerCase();
  if (/failed to fetch|load failed|networkerror|err_network/i.test(m)) return true;
  if (/\b(429|502|503|504)\b/.test(m)) return true;
  if (/timeout|timed out|too many requests/i.test(m)) return true;
  return false;
}
// Dev helper : ?debug-error=network|permanent dans l'URL force l'affichage
// du toast inline ou du fullscreen sans avoir à simuler un vrai network fail.
// N'est actif qu'en local (NODE_ENV !== "production") pour éviter toute
// exposition en prod. Réutilise la `route` déjà déclarée en haut du fichier.
const debugErrorMode = computed(() => {
  if (process.env.NODE_ENV === "production") return null;
  const v = route.query["debug-error"];
  return typeof v === "string" ? v : null;
});
const isRecoverableFatalError = computed(() => {
  if (debugErrorMode.value === "network") return true;
  return stripeInitError.value && isRecoverableErrMsg(stripeErrorMessage.value);
});
const inlineBannerDismissed = ref(false);
watch(stripeErrorMessage, () => {
  inlineBannerDismissed.value = false;
});
const showInlineErrorBanner = computed(
  () => isRecoverableFatalError.value && !inlineBannerDismissed.value,
);
const retryingInlineError = ref(false);
function retryInlineError() {
  retryingInlineError.value = true;
  if (typeof window !== "undefined") window.location.reload();
}
const campaignInfo = campaignData.campaignInfo;
/** « 8 octobre » — le jour de fin en clair, pour la barre de temps. */
const endDateLabel = computed(() => {
  const jour = jourDeFinCagnotte(campaignInfo.value?.endDate);
  if (!jour) return null;
  const memeAnnee = jour.slice(0, 4) === String(new Date().getFullYear());
  return formatInParis(jour, { day: "numeric", month: "long", ...(memeAnnee ? {} : { year: "numeric" }) });
});
/**
 * La barre est arrivée à zéro : on relit la cagnotte — l'API refuse alors le
 * don (« terminée ») et la page affiche ce message à la place du formulaire.
 */
function onCampaignEnded() {
  void campaignData.refreshData();
}
const organizationLogoUrl = campaignData.organizationLogoUrl;
const isPaypalOnlyMode = paypalDonation.isPaypalOnlyMode;
const showPayPalAlternative = paypalDonation.showPayPalAlternative;
const paypalCheckout = paypalDonation.checkout;

// Le formulaire carte suit le provider PRIMAIRE choisi par l'org
// (preferred_provider). Cette page n'utilise pas l'orchestrateur → on dérive
// directement depuis payment_providers : PayPal-only, OU (les 2 actifs ET
// preferred=paypal). Les wallets Stripe restent dispo dès que Stripe existe.
// Provider du FORMULAIRE CARTE (Karim 2026-08-30) : `preferred_provider` = où
// l'asso veut recevoir l'argent (bouton PayPal, wallets), mais il n'impose les
// CardFields PayPal que si PayPal sait les rendre (capability PPCP_CUSTOM).
// Sinon, Stripe actif → carte Stripe. Jamais de cadre vide. Règle unique dans
// `resolveCardProvider` (@donoor/checkout) — ne PAS la réimplémenter ici.
const usesPaypalCard = computed(() => {
  const pp = campaignData.paymentProviders.value;
  return (
    resolveCardProvider({
      stripe: pp.stripe.charges_enabled,
      paypal: pp.paypal.charges_enabled,
      paypalCardFields: isPaypalCardFieldsUsable(pp.paypal),
      preferred: pp.preferred_provider,
    }) === "paypal"
  );
});
// Wallets (Apple/Google Pay) suivent le provider PRIMAIRE
// (Karim 2026-06-21) : preferred=stripe → Stripe Express Checkout ;
// preferred=paypal → PayPal Applepay/Googlepay séparés. PayPal-only
// reste vrai. Le routing aval passe par usesPaypalWallets.
const usesPaypalWallets = computed(
  () => isPaypalOnlyMode.value || usesPaypalCard.value,
);

// Composable de mount unifié — route Stripe Express OU PayPal Apple+Google
// selon le provider primaire (Karim 2026-06-21).
const { mountPrimaryWallets } = useWalletsMount({
  stripeCheckout: checkout,
  paypalCheckout,
  isPaypalOnlyMode,
  usesPaypalWallets,
});
const campaignImageUrl = campaignData.campaignImageUrl;
const progressPercentage = campaignData.progressPercentage;
const isLoading = paymentForm.isLoading;
const form = paymentForm.form;
const suggestedAmounts = paymentForm.suggestedAmounts;
const _donationType = paymentForm.donationType;
const _subscriptionPeriod = paymentForm.subscriptionPeriod;
const _subscriptionPeriods = paymentForm.subscriptionPeriods;
const includeFees = paymentForm.includeFees;
const showMessage = paymentForm.showMessage;
const touched = paymentForm.touched;
const errors = paymentForm.errors;
const isSubscription = paymentForm.isSubscription;
const finalAmount = calculations.finalAmount;
const feesAmount = calculations.feesAmount;
const finalAmountWithDiscount = calculations.finalAmountWithDiscount;
const formatAmount = calculations.formatAmount;
const validateEmail = paymentForm.validateEmail;
const validateName = paymentForm.validateName;
const validateAmount = paymentForm.validateAmount;

// Flag "Soutenir Donoor"
const donateUs = campaignData.donateUs;
const showFiscalInfo = campaignData.showFiscalInfo;
// Mention du pays (66 %, 30 % en Belgique, déduction au Luxembourg) — 2026-10-30.
// Lue dans la réponse déjà chargée par useCampaignDonationData (même clé).
const { data: donneesDonCampagne } = useNuxtData<{ data?: { fiscalRegime?: string | null } }>(`campaign-donation-${campaignSlug}`);
fournirRegimeFiscal(() => donneesDonCampagne.value?.data?.fiscalRegime);

// Options héritées de la "page de paiement" de l'orga
const showFeesOption = campaignData.showFeesOption;
const _subscriptionsEnabled = campaignData.subscriptionsEnabled;
const suggestedAmountsFromOrg = campaignData.suggestedAmounts;
const defaultAmountIndex = campaignData.defaultAmountIndex;

// Pré-sélectionner le montant par défaut configuré par l'orga
watch(
  [suggestedAmountsFromOrg, defaultAmountIndex],
  ([amounts, idx]) => {
    // Montant déjà choisi sur la page de la cagnotte : on ne l'écrase pas.
    if (montantDemande !== null) return;
    if (!amounts || amounts.length === 0) return;
    const safeIdx = Math.min(Math.max(0, idx ?? 0), amounts.length - 1);
    const target = String(amounts[safeIdx] ?? amounts[0]);
    const current = paymentForm.form.value.amount;
    // Ne réécrit que si la valeur courante est vide ou un défaut générique
    if (!current || current === "10" || current === "20") {
      paymentForm.form.value.amount = target;
    }
  },
  { immediate: true },
);
const customFields = campaignData.customFields;
// Pour DonateDonorForm : on retire "message" car on l'affiche dans le récap (colonne gauche)
const customFieldsWithoutMessage = computed(() => {
  const list = customFields.value;
  if (!list) return null;
  return list.filter((f) => f.type !== "message");
});
const askFullName = campaignData.askFullName;
const _requireFullName = campaignData.requireFullName;
const askEmail = campaignData.askEmail;
const _requireEmail = campaignData.requireEmail;
const askMessage = campaignData.askMessage;
// Zone de message OUVERTE d'emblée quand la cagnotte l'autorise (Karim
// 2026-09-30) ; « Supprimer » la replie.
watch(askMessage, (autorise) => { if (autorise) showMessage.value = true; }, { immediate: true });
const primaryColor = campaignData.primaryColor;
const buttonStyle = campaignData.buttonStyle;
const submitText = campaignData.submitText;
// Montants d'impact de la cagnotte (« 50 € — 1 m² de toiture »).
const impactOptions = campaignData.impactOptions;
const impactPeriodSuffix = computed(() =>
  suffixePeriodeImpact(isSubscription.value, paymentForm.subscriptionPeriod.value, t),
);

// Override isValidForm : si l'orga a retiré email/nom des customFields, ne pas
// les exiger pour activer le bouton de paiement.
const isValidForm = computed(() => {
  const hasAmount = parseFloat(form.value.amount) > 0;
  if (!hasAmount) return false;

  if (askEmail.value) {
    const ok =
      form.value.donorEmail.trim() !== "" &&
      isValidEmail(form.value.donorEmail);
    if (!ok) return false;
  }

  if (askFullName.value && !form.value.isAnonymous) {
    if (!form.value.donorName.trim()) return false;
  }

  return true;
});

// Texte conditionnel pour les frais
const feesText = computed(() => {
  const hasDonoorFees = campaignData.donoorFeeRate.value > 0;
  return hasDonoorFees ? t("don.paySlugDisplay.fraisStripeEtDonoor") : "Stripe";
});

// darkenColor importé depuis @donoor/utils/color (source unique cross-app).

// État pour l'affichage des erreurs globales
const globalError = ref<string | null>(null);

// Valider tous les champs (skip ceux retirés par l'orga des customFields)
const validateAll = () => {
  validateAmount();
  if (askEmail.value) validateEmail();
  if (askFullName.value) validateName();
};

// Initialiser le WebSocket pour le paiement
const paymentSocket = usePaymentSocket();

// Initialiser le WebSocket pour les mises à jour campagne en temps réel
const {
  connect: connectCampaignSocket,
  joinCampaignRoom,
  onCampaignDonation,
  disconnect: disconnectCampaignSocket,
} = useCampaignSocket();

// Variable pour suivre si on a déjà redirigé
let hasRedirected = false;

// Helper de rechargement SSR-safe
const reloadPage = () => {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
};

// Retour à la page de la campagne
const goBackToCampaign = () => {
  if (typeof window !== "undefined") {
    router.push(`/c/${campaignSlug}`);
  }
};

// Format de montant pour l'affichage
const formatAmountShort = (amount: number) =>
  formatCurrency(amount, { decimals: 0 });

// Soumission du formulaire (paiement carte via @donoor/checkout)
// Moyens de paiement LOCAUX (Bancontact d'une association belge — étape B
// « autres pays ») : absents pour une association française, et pour un don
// mensuel (la banque redirige ; la carte reste là pour ça).
const {
  moyensLocaux,
  identifiantOfficiel,
  erreurMoyenLocal,
  erreurTitulaireMoyenLocal,
  erreurEmailMoyenLocal,
  payerParMoyenLocal,
  prelevement,
} = useMoyensLocauxDon({
  paymentProviders: campaignData.paymentProviders,
  masquer: computed(() => false),
  // Don mensuel : seul le prélèvement SEPA reste proposé (02/11/2026).
  donMensuel: computed(() => isSubscription.value),
  // Formulaire de prélèvement de Stripe (IBAN + mandat).
  prelevementSepa: stripeDonation.prelevementSepa,
  // Champs du donateur contrôlés et signalés en rouge ; le message général
  // va sous le bouton du moyen, PAS sous la carte (Karim 21/10 : « j'ai mis
  // la carte mais elle est en rouge »).
  formulaireValide: () => {
    if (askEmail.value) validateEmail();
    if (!isValidForm.value || (askEmail.value && errors.email)) {
      validateAll();
      return false;
    }
    return true;
  },
  payer: stripeDonation.payerParMoyenLocal,
  isLoading,
  messageGenerique: () => t("don.checkoutApp.erreurPaiementGenerique"),
  messageFormulaireInvalide: () => t("don.checkoutApp.erreurValidation"),
  messageNomManquant: (moyen) => t(cleTexteTitulaire("nomEnHaut", moyen), { moyen: MOYENS_PAIEMENT_LOCAUX[moyen].nom }),
  messageNomInvalide: (moyen) => t(cleTexteTitulaire("nomRequis", moyen), { moyen: MOYENS_PAIEMENT_LOCAUX[moyen].nom }),
  messageEmailInvalide: () => t("don.moyensLocaux.emailInvalide"),
  // En direct : l'association allume ou éteint un moyen, le bouton suit (01/11/2026).
  enDirect: { socket: paymentSocket, organizationId: campaignData.organizationId },
  // Link allumé / éteint par l'association : Stripe retire ou remet le bouton,
  // la page remonte son paiement express pour le voir aussitôt (02/11/2026).
  surChangementLink: () => checkout.rafraichirExpressCheckout(),
});

const handleSubmit = async () => {
  if (askEmail.value) validateEmail();
  if (!isValidForm.value || (askEmail.value && errors.email)) {
    // JAMAIS de return muet : sans message, l'utilisateur clique et
    // « rien ne se passe ». On valide tous les champs (affiche les
    // erreurs inline) + un message global. Karim 2026-07-02.
    validateAll();
    globalError.value = t("don.checkoutApp.erreurValidation");
    return;
  }

  isLoading.value = true;
  globalError.value = null;

  try {
    // Dispatch selon le provider actif.
    const { error } = usesPaypalCard.value
      ? await paypalCheckout.confirmCardPayment()
      : await checkout.confirmCardPayment();

    if (error) {
      globalError.value =
        error.message || t("don.checkoutApp.erreurPaiementGenerique");
      isLoading.value = false;
      return;
    }

    // Si on arrive ici sans erreur, le redirect est en cours
    if (currentPaymentIntentId.value) {
      paymentSocket.joinPaymentRoom(currentPaymentIntentId.value);
    }
  } catch (error: unknown) {
    logger.error("Erreur critique lors du paiement:", error);
    globalError.value =
      (error instanceof Error ? error.message : null) ||
      t("don.pageDonate.erreurInattendueReessayer");
    isLoading.value = false;
  }
};

// Désactiver le dark mode uniquement sur cette page
const previousHadDark = ref(false);

onMounted(async () => {
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    previousHadDark.value = root.classList.contains("dark");
    root.classList.remove("dark");
  }

  // ✅ SSR: Les données sont déjà chargées côté serveur via useFetch
  await campaignData.loadCampaignData();

  paymentSocket.connect();

  // ✅ Connecter le WebSocket campagne pour les mises à jour en temps réel
  connectCampaignSocket();
  if (campaignInfo.value.id) {
    joinCampaignRoom(campaignInfo.value.id);
    onCampaignDonation((data) => {
      campaignData.updateRealtimeData(data.newTotalAmount, data.donorCount);
      logger.log(`🎯 Don reçu en temps réel: ${data.amount}€ - Total: ${data.newTotalAmount}€`);
    });
  }

  paymentSocket.onPaymentConfirmed((data) => {
    if (hasRedirected) return;
    hasRedirected = true;
    router.push({
      path: "/donation-success",
      query: {
        payment_intent_id: data.paymentIntentId,
        transaction_id: data.transactionId,
        amount: data.amount,
        email: form.value.donorEmail,
        campaign_id: campaignInfo.value.id,
        campaign_title: campaignInfo.value.title,
        thank_you_message: campaignInfo.value.customThankYouMessage || "",
      },
    });
  });

  // Mount : formulaire carte selon le provider primaire ; wallets via
  // useWalletsMount (route Stripe Express OU PayPal Apple+Google selon
  // preferred — Karim 2026-06-21).
  await nextTick();
  if (usesPaypalCard.value) {
    try {
      await paypalCheckout.mountCardForm("paypal-cardfields-container");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[C Payment] PayPal mountCardForm error:", msg);
    }
  } else {
    await checkout.mountCardForm("card-element", { type: "card-legacy" });
  }
  await mountPrimaryWallets();
});

// Mettre à jour le tip Donoor quand le montant du don change
watch(() => paymentForm.form.value.amount, (newAmount) => {
  donateToUsAmount.value = getDonoorTipAmount(Number(newAmount) || 0);
});

onBeforeUnmount(() => {
  if (typeof document !== "undefined" && previousHadDark.value) {
    document.documentElement.classList.add("dark");
  }
  // Déconnecter le WebSocket campagne
  disconnectCampaignSocket();
});
</script>

<template>
  <div class="relative">
    <!-- Langue de la page, en haut à droite sur ordinateur ; sur mobile,
         sur la ligne du nom de l'association (Karim 01/11/2026). -->
    <div class="hidden lg:block absolute top-3 right-3 z-30">
      <SelecteurLangue variante="clair" sens="bas" />
    </div>
    <!-- Cascade d'erreurs unifiée. Ajoute aussi OrgUnavailable (jusqu'ici
         absent de cette page — alignement avec les 3 autres pages de don). -->
    <DonateErrorStates
      v-if="!pageLoading"
      :org-risk-status="campaignData.organizationRiskStatus.value"
      :org-risk-reason="campaignData.organizationRiskReason.value"
      :organization-name="campaignInfo.organization"
      :is-campaign-paused="isCampaignPaused"
      :campaign-paused-message="campaignError"
      :on-paused-back="goBackToCampaign"
      :is-entity-invalid="!isCampaignValid && !isCampaignPaused"
      invalid-entity-kind="campaign"
      :invalid-title="errorTitle"
      :invalid-message="campaignError"
      :no-payment-provider-active="!campaignData.canAcceptPayments.value"
      :payments-pending="campaignData.isPaymentsPending.value"
      :stripe-init-error="stripeInitError"
      :stripe-error-message="stripeErrorMessage"
      :is-paypal-only-mode="isPaypalOnlyMode"
      :is-recoverable="isRecoverableFatalError"
      @reload="reloadPage"
    />

    <!-- Modal centré pour erreurs RÉCUPÉRABLES (network/429/5xx).
         Composant extrait : CampaignPaymentRecoverableErrorModal. -->
    <CampaignPaymentRecoverableErrorModal
      :show="showInlineErrorBanner"
      :error-message="stripeErrorMessage"
      :retrying="retryingInlineError"
      @dismiss="inlineBannerDismissed = true"
      @retry="retryInlineError"
    />

    <DonationPageSkeleton v-if="pageLoading" />

    <!-- Contenu principal — partagé Stripe / PayPal-only / cohabitation.
         Page de paiement cagnotte identique entre providers (mêmes
         composants, mêmes couleurs). Seul le bloc de paiement varie. -->
    <div
      v-if="
        !pageLoading &&
        !campaignData.isOrgBlocked.value &&
        isCampaignValid &&
        (!stripeInitError || isPaypalOnlyMode)
      "
      class="min-h-screen bg-white"
      :style="{
        '--payment-primary': primaryColor || '#3B82F6',
        '--payment-primary-hover': darkenColor(primaryColor || '#3B82F6', 15),
      }"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mx-auto">
        <!-- Colonne de gauche - Info Campagne -->
        <div class="order-1 px-4 sm:px-6 lg:pl-15 xl:pl-60 pr-5 py-2 lg:py-14">
          <div class="sticky top-8">
            <!-- Desktop Campaign Info -->
            <!-- Desktop Campaign Info -->
            <CampaignPaymentDesktopSummary
              v-model:message="form.message"
              v-model:show-message="showMessage"
              :organization-logo-url="organizationLogoUrl"
              :campaign-info="campaignInfo"
              :is-dev="isDev"
              :format-amount="formatAmount"
              :format-amount-short="formatAmountShort"
              :final-amount="finalAmount"
              :detail-association="detailAssociation"
              :prestataire="estimateProvider"
              :final-amount-with-discount="finalAmountWithDiscount"
              :campaign-image-url="campaignImageUrl"
              :progress-percentage="progressPercentage"
              :primary-color="primaryColor"
              :include-fees="includeFees"
              :fees-amount="feesAmount"
              :donate-us="donateUs"
              :donate-to-us-amount="donateToUsAmount"
              :ask-message="askMessage"
              :closes-at="campaignInfo.closesAt"
              :end-date-label="endDateLabel"
              :days-remaining="campaignInfo.daysRemaining"
              :starts-at="campaignInfo.startsAt"
              @ended="onCampaignEnded"
            >
              <!-- Réduction d'impôt : juste sous la progression (objectif +
                   temps), toujours visible en haut — Karim 2026-10-22. -->
              <template #apres-progression>
                <DonateFiscalBanner
                  v-if="showFiscalInfo"
                  :final-amount="finalAmount"
                  :format-amount="formatAmount"
                  :is-subscription="isSubscription"
                />
              </template>
            </CampaignPaymentDesktopSummary>

            <!-- Mobile Header -->
            <CampaignPaymentMobileHeader
              :organization-logo-url="organizationLogoUrl"
              :campaign-info="campaignInfo"
              :campaign-image-url="campaignImageUrl"
              :format-amount="formatAmount"
              :format-amount-short="formatAmountShort"
              :final-amount-with-discount="finalAmountWithDiscount"
              :progress-percentage="progressPercentage"
              :closes-at="campaignInfo.closesAt"
              :end-date-label="endDateLabel"
              :days-remaining="campaignInfo.daysRemaining"
              :starts-at="campaignInfo.startsAt"
              @ended="onCampaignEnded"
            >
              <template #apres-progression>
                <DonateFiscalBanner
                  v-if="showFiscalInfo"
                  :final-amount="finalAmount"
                  :format-amount="formatAmount"
                  :is-subscription="isSubscription"
                />
              </template>
            </CampaignPaymentMobileHeader>
          </div>
        </div>

        <!-- Colonne de droite - Formulaire de paiement -->
        <div class="order-2 px-4 sm:px-6 lg:px-14 pb-6 lg:py-14 md:shadow-2xl lg:min-h-screen bg-white">
          <div class="max-w-md mx-auto lg:mx-0">
            <!-- Express Checkout Element pour Apple Pay et Google Pay -->
            <!-- Express Checkout : Stripe wallets + PayPal alternative
                 selon les providers actifs sur l'org. -->
            <DonateExpressCheckoutSection
              :stripe-checkout="checkout"
              :paypal-checkout="paypalCheckout"
              :payment-form="paymentForm"
              :is-paypal-only-mode="isPaypalOnlyMode"
              :show-pay-pal-alternative="showPayPalAlternative"
              :is-subscription="isSubscription"
              :uses-paypal-wallets="usesPaypalWallets"
              paypal-container-id="paypal-alt-buttons-c-payment"
            />

            <!-- iDEAL | Wero (Pays-Bas), Bancontact (Belgique) : paiement express, sous
                 Apple Pay / Google Pay (Karim 01/11/2026). Rien pour la France. -->
            <DonateMoyensLocaux
              :moyens="moyensLocaux"
              :nom-donateur="form.isAnonymous ? '' : form.donorName"
              :nom-demande-en-haut="champNomAffiche(customFieldsWithoutMessage, form.isAnonymous)"
              :anonyme="form.isAnonymous"
              :email="form.donorEmail"
              :email-requis="askEmail"
              :montant="finalAmount > 0 ? formatAmount(finalAmount) : null"
              :is-loading="isLoading"
              :erreur="erreurMoyenLocal"
              :erreur-nom="erreurTitulaireMoyenLocal"
              :erreur-email="erreurEmailMoyenLocal"
              :prelevement="prelevement"
              :mensuel="isSubscription"
              @update:email="(v: string) => (form.donorEmail = v)"
              @update:nom-donateur="(v: string) => (form.donorName = v)"
              @payer="payerParMoyenLocal"
            />

            <!-- Séparateur "Ou payer par carte" — affiché Stripe et PayPal -->
            <div class="hidden lg:block relative mb-6">
              <div class="absolute inset-0 flex items-center">
                <div
                  class="w-full border-t border-gray-300"
                />
              </div>
              <div class="relative flex justify-center text-sm">
                <span
                  class="px-2 bg-white text-gray-500"
                >
                  {{ t('campagne.pageCampaignsDonate.ouPayerParCarte') }}
                </span>
              </div>
            </div>

            <!-- Options (Couvrir les frais + Soutenir Donoor) -->
            <DonateOptionsGroup
              v-model:include-fees="includeFees"
              v-model:donate-to-us-amount="donateToUsAmount"
              :fees-amount="feesAmount"
              :final-amount="finalAmount"
              :is-subscription="isSubscription"
              :fees-text="feesText"
              :format-amount="formatAmount"
              :donate-us="donateUs"
              :show-fees-option="showFeesOption"
              :organization-name="campaignInfo.organization"
              :provider-label="usesPaypalCard ? 'paypal' : 'stripe'"
              class="mb-4 lg:mb-6"
            />

            <!-- Formulaire -->
            <form class="space-y-6" @submit.prevent="handleSubmit">
              <!-- Montant -->
              <DonateAmountSelector
                data-montant-don
                v-model:amount="form.amount"
                :suggested-amounts="suggestedAmountsFromOrg ?? suggestedAmounts"
                :format-amount="formatAmount"
                :has-error="touched.amount && !!errors.amount"
                :error-message="errors.amount"
                :primary-color="primaryColor"
                :button-style="buttonStyle"
              />

              <DonateImpactOptions
                v-model:amount="form.amount"
                :options="impactOptions"
                :format-amount="formatAmount"
                :primary-color="primaryColor"
                :period-suffix="impactPeriodSuffix"
              />

              <!-- Champs CB intégrés — Stripe Elements ou PayPal CardFields -->
              <DonateCardElement
                :is-dev="isDev"
                :is-initialized="usesPaypalCard ? (paypalCheckout.cardMounted.value || !!paypalCheckout.cardFormError.value) : (checkout.cardMounted.value || !!checkout.cardFormError.value)"
                :provider="usesPaypalCard ? 'paypal' : 'stripe'"
                :mount-error-code="
                  usesPaypalCard ? (paypalCheckout.cardFormError.value?.code ?? null) : null
                "
              />

              <!-- Informations donateur -->
              <DonateDonorForm
                v-model:donor-email="form.donorEmail"
                v-model:donor-name="form.donorName"
                v-model:is-anonymous="form.isAnonymous"
                v-model:message="form.message"
                :touched="touched"
                :errors="errors"
                :show-message-field="askMessage"
                :custom-fields="customFieldsWithoutMessage"
                @validate-email="validateEmail"
                @validate-name="validateName"
              />

              <!-- Bouton "Donner" — disponible pour Stripe ET PayPal -->
              <DonateSubmitButton
                :is-valid-form="isValidForm"
                :is-loading="isLoading"
                :is-subscription="false"
                :global-error="globalError"
                :organization-name="campaignInfo.organization"
                :submit-text="submitText"
                :primary-color="primaryColor"
                :button-style="buttonStyle"
                :identifiant-officiel="identifiantOfficiel"
                :providers-label="libelleSecurite"
                @validate-all="validateAll"
              />
            </form>

            <!-- Footer -->
            <DonateFooter />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Styles pour le contenu HTML de la description de campagne */
.campaign-description :deep(p) {
  margin: 0.5rem 0;
}

.campaign-description :deep(strong) {
  font-weight: 600;
  color: #374151;
}

.campaign-description :deep(u) {
  text-decoration: underline;
}

.campaign-description :deep(em) {
  font-style: italic;
}

.campaign-description :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.campaign-description :deep(a:hover) {
  color: #1d4ed8;
}

.campaign-description :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1rem 0;
}

.campaign-description :deep(br) {
  display: block;
  content: "";
  margin-top: 0.25rem;
}
</style>
